const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const EXPLICIT_TIMEZONE_PATTERN = /(?:Z|[+-]\d{2}:\d{2})$/i;
const WALL_CLOCK_PATTERN =
  /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?)$/;

export function getClientTimezone(fallback = "UTC"): string {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezone?.trim() ? timezone : fallback;
  } catch {
    return fallback;
  }
}

function isValidTimezone(timezone: string | null | undefined): timezone is string {
  if (!timezone) return false;
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

export function isDateOnlyString(value: string | null | undefined): boolean {
  return typeof value === "string" && DATE_ONLY_PATTERN.test(value.trim());
}

function getTimeZoneDateParts(date: Date, timezone: string): {
  year: string;
  month: string;
  day: string;
} {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const year = parts.find((part) => part.type === "year")?.value ?? "1970";
  const month = parts.find((part) => part.type === "month")?.value ?? "01";
  const day = parts.find((part) => part.type === "day")?.value ?? "01";

  return { year, month, day };
}

function formatDateInTimezone(date: Date, timezone: string): string {
  const { year, month, day } = getTimeZoneDateParts(date, timezone);
  return `${year}-${month}-${day}`;
}

function parseInstant(value: string | Date): Date {
  const parsed = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error("Invalid instant value");
  }
  return parsed;
}

export function getBusinessTimezone(fallback = "UTC"): string {
  return getClientTimezone(fallback);
}

export function getDisplayTimezone(fallback = "UTC"): string {
  return getClientTimezone(fallback);
}

function getTimezoneOffsetMinutes(date: Date, timezone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  const year = Number(parts.find((part) => part.type === "year")?.value ?? "1970");
  const month = Number(parts.find((part) => part.type === "month")?.value ?? "01");
  const day = Number(parts.find((part) => part.type === "day")?.value ?? "01");
  const hour = Number(parts.find((part) => part.type === "hour")?.value ?? "00");
  const minute = Number(parts.find((part) => part.type === "minute")?.value ?? "00");
  const second = Number(parts.find((part) => part.type === "second")?.value ?? "00");

  const utcMillis = Date.UTC(year, month - 1, day, hour, minute, second);
  return Math.round((utcMillis - date.getTime()) / 60000);
}

function parseTimeParts(timeStr: string): {
  hours: number;
  minutes: number;
  seconds: number;
  milliseconds: number;
} {
  const match = timeStr.trim().match(/^(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?$/);
  if (!match) {
    throw new Error("Invalid wall-clock input");
  }

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  const seconds = Number(match[3] ?? "0");
  const milliseconds = Number((match[4] ?? "0").padEnd(3, "0"));

  if (
    hours < 0 || hours > 23 ||
    minutes < 0 || minutes > 59 ||
    seconds < 0 || seconds > 59 ||
    milliseconds < 0 || milliseconds > 999
  ) {
    throw new Error("Invalid wall-clock input");
  }

  return { hours, minutes, seconds, milliseconds };
}

export function buildUtcInstantFromWallClock(
  dateStr: string,
  timeStr: string,
  timezone: string
): string {
  if (!dateStr.trim() || !timeStr.trim() || !isValidTimezone(timezone)) {
    throw new Error("Invalid wall-clock input");
  }

  const [year, month, day] = dateStr.split("-").map(Number);
  const { hours, minutes, seconds, milliseconds } = parseTimeParts(timeStr);

  const approximateUtc = new Date(Date.UTC(
    year,
    month - 1,
    day,
    hours,
    minutes,
    seconds,
    milliseconds
  ));
  const offsetMinutes = getTimezoneOffsetMinutes(approximateUtc, timezone);
  const utcInstant = new Date(approximateUtc.getTime() - offsetMinutes * 60_000);

  return utcInstant.toISOString();
}

export function normalizeMutationDateValue(
  value: string | Date | null | undefined,
  timezone = getDisplayTimezone()
): string | undefined {
  if (value == null || value === "") return undefined;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      throw new Error("Invalid mutation date value");
    }
    return value.toISOString();
  }

  const trimmed = value.trim();
  if (!trimmed) return undefined;

  if (isDateOnlyString(trimmed)) {
    return trimmed;
  }

  if (EXPLICIT_TIMEZONE_PATTERN.test(trimmed)) {
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      throw new Error("Invalid mutation date value");
    }
    return parsed.toISOString();
  }

  const wallClockMatch = trimmed.match(WALL_CLOCK_PATTERN);
  if (wallClockMatch) {
    const [, datePart, timePart] = wallClockMatch;
    return buildUtcInstantFromWallClock(datePart, timePart, timezone);
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    throw new Error("Invalid mutation date value");
  }

  return parsed.toISOString();
}

export function formatInstantInTimezone(
  value: string | Date,
  timezone: string,
  options: Intl.DateTimeFormatOptions,
  locale?: Intl.LocalesArgument
): string {
  const date = parseInstant(value);
  return new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    ...options,
  }).format(date);
}

export function formatInstantTo12Hour(value: string | Date, timezone: string): string {
  return formatInstantInTimezone(
    value,
    timezone,
    { hour: "numeric", minute: "2-digit", hour12: true },
    "en-US"
  );
}

export function getDateKeyInTimezone(value: string | Date, timezone: string): string {
  return formatDateInTimezone(parseInstant(value), timezone);
}
